import {countsAsProvided} from '@sollapay/domain';
import {useEffect, useMemo} from 'react';

import {INITIAL_FORM_STATE} from '@/hooks';

import {useDocumentsQuery} from './useDocuments';

import type {FormStateSnapshot} from '@/hooks';
import type {DocumentContext, DocumentEntityType, DocumentType} from '@sollapay/enums';

interface UseDocumentRequiredFormParams {
  entityType: DocumentEntityType;
  entityId: string;
  documentContext: DocumentContext;
  requiredDocumentTypes: DocumentType[];
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

/**
 * Tracks whether every required document has been uploaded and reports the
 * result via `onFormStateChange`. Use this for steps that have no form
 * fields — only mandatory document uploads.
 */
export const useDocumentRequiredForm = ({
  entityType,
  entityId,
  documentContext,
  requiredDocumentTypes,
  onFormStateChange
}: UseDocumentRequiredFormParams) => {
  const documentsQuery = useDocumentsQuery(entityType, entityId, documentContext);

  // Only a successful fetch proves absence — in-flight or failed means "unknown", not "none".
  const missingDocumentTypes = useMemo(() => {
    const documents = documentsQuery.data;
    if (documents === undefined) return new Set<DocumentType>();
    const uploadedTypes = new Set(documents.filter(countsAsProvided).map(doc => doc.documentType));
    return new Set(requiredDocumentTypes.filter(type => !uploadedTypes.has(type)));
  }, [documentsQuery.data, requiredDocumentTypes]);

  // Don't report the query's loading/error state to the modal — document
  // fetching is silent and should never hide form content or show a spinner.
  const hasErrors = missingDocumentTypes.size > 0;

  useEffect(() => {
    onFormStateChange?.({...INITIAL_FORM_STATE, hasErrors});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors]);

  return {hasErrors, missingDocumentTypes};
};
